import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  NewspaperIcon,
  ShoppingBagIcon,
  SparklesIcon,
  TrophyIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const navigationGroups = [
  {
    label: "DISCOVER",
    items: [
      { label: "Home", path: "/home", icon: HomeIcon },
      { label: "News", path: "/news", icon: NewspaperIcon },
      { label: "Sports", path: "/sports", icon: TrophyIcon },
    ],
  },
  {
    label: "COMMUNITY",
    items: [
      { label: "Voting", path: "/voting", icon: SparklesIcon },
      { label: "Lifestyle", path: "/lifestyle", icon: UserCircleIcon },
      { label: "Magazines", path: "/magazines", icon: NewspaperIcon },
      { label: "Pricing", path: "/subscription-plans", icon: ShoppingBagIcon, publicOnly: true },
    ],
  },
];

function getActiveItem(pathname, isAuthenticated) {
  if (pathname === "/" || pathname === "/home") return "Home";
  if (pathname.startsWith("/news")) return "News";
  if (
    ["/sports", "/live-matches", "/upcoming-matches", "/highlights", "/players", "/clubs", "/league-tables", "/stats"].some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    )
  ) return "Sports";
  if (pathname.startsWith("/lifestyle")) return "Lifestyle";
  if (pathname.startsWith("/magazines") || pathname.startsWith("/magazine-viewer/")) return "Magazines";
  if (pathname.startsWith("/voting")) return "Voting";
  if (pathname.startsWith("/subscription-plans")) return "Pricing";
  if (pathname.startsWith("/buy-votes")) return "Buy Votes";
  if (pathname.startsWith("/profile")) return isAuthenticated ? "Profile" : "";
  return "";
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );
  const isAuthenticated = localStorage.getItem("token") !== null;
  const navigate = useNavigate();
  const location = useLocation();
  const activeItem = getActiveItem(location.pathname, isAuthenticated);
  const compact = isDesktop && isCollapsed;
  const displayGroups = useMemo(
    () => navigationGroups.map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.publicOnly || !isAuthenticated),
    })),
    [isAuthenticated]
  );

  useEffect(() => {
    const updateViewport = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const updateLayout = () => {
      const isHomePage = location.pathname === "/" || location.pathname === "/home";
      const useSidebar = isAuthenticated && isHomePage && window.innerWidth >= 768;
      document.documentElement.classList.toggle("desac-sidebar-layout", useSidebar);
      document.documentElement.classList.toggle(
        "desac-sidebar-collapsed",
        useSidebar && (window.innerWidth < 1024 || isCollapsed)
      );
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => {
      window.removeEventListener("resize", updateLayout);
      document.documentElement.classList.remove("desac-sidebar-layout", "desac-sidebar-collapsed");
    };
  }, [isAuthenticated, isCollapsed, location.pathname]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const navigateTo = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const toggleCollapsed = () => {
    setIsCollapsed((collapsed) => {
      const nextCollapsed = !collapsed;
      window.localStorage.setItem("desac-sidebar-collapsed", String(nextCollapsed));
      window.dispatchEvent(new CustomEvent("desac-sidebar-toggle", { detail: { collapsed: nextCollapsed } }));
      return nextCollapsed;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    navigate("/");
    window.location.reload();
  };

  const renderNavigationItems = () => (
    <>
      {displayGroups.map((group) => (
        <section key={group.label} className="mb-5">
          {!compact && (
            <h2 className="mb-2 px-3 text-[10px] font-semibold tracking-[0.16em] text-gray-500">
              {group.label}
            </h2>
          )}
          <ul className="space-y-1">
            {group.items.map(({ label, path, icon: Icon }) => {
              const selected = activeItem === label;
              return (
                <li key={label}>
                  <button
                    type="button"
                    title={compact ? label : undefined}
                    aria-current={selected ? "page" : undefined}
                    onClick={() => navigateTo(path)}
                    className={`group flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                      compact ? "justify-center" : "gap-3"
                    } ${
                      selected
                        ? "bg-accent/10 text-white ring-1 ring-inset ring-accent/20"
                        : "text-gray-400 hover:bg-surface-800 hover:text-white active:bg-surface-700"
                    }`}
                  >
                    <Icon
                      aria-hidden="true"
                      className={`h-5 w-5 shrink-0 transition-colors ${
                        selected ? "text-accent-soft" : "text-gray-500 group-hover:text-gray-300"
                      }`}
                    />
                    {!compact && <span className="truncate">{label}</span>}
                    {!compact && selected && (
                      <span aria-hidden="true" className="ml-auto h-1.5 w-1.5 rounded-full bg-accent" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );

  if (!isAuthenticated) {
    return (
      <header className="fixed left-0 top-0 z-50 h-16 w-full border-b border-line bg-surface-950/95 backdrop-blur">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6">
          <Brand />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigateTo("/login")} className="btn-ghost">Log In</button>
            <button type="button" onClick={() => navigateTo("/sign-up")} className="btn-primary">Sign Up</button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Compact mobile app bar; the drawer is available below the tablet breakpoint. */}
      <header className="fixed left-0 top-0 z-50 flex h-16 w-full items-center justify-between border-b border-line bg-surface-950/95 px-4 backdrop-blur md:hidden">
        <Brand />
        <button
          type="button"
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isOpen}
          aria-controls="desac-user-navigation"
          onClick={() => setIsOpen((open) => !open)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-300 transition-colors hover:bg-surface-800 hover:text-white"
        >
          {isOpen ? <XMarkIcon aria-hidden="true" className="h-5 w-5" /> : <Bars3Icon aria-hidden="true" className="h-5 w-5" />}
        </button>
      </header>

      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-[1px] md:hidden"
        />
      )}

      <aside
        id="desac-user-navigation"
        aria-label="User navigation"
        className={`fixed bottom-0 left-0 top-16 z-[60] flex w-[min(288px,calc(100vw-48px))] flex-col border-r border-line bg-surface-950 transition-[transform,width] duration-200 ease-out md:top-0 md:z-50 md:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } ${
          compact ? "md:w-20" : "md:w-64"
        }`}
      >
        <div className={`hidden h-16 shrink-0 items-center border-b border-line px-4 md:flex ${compact ? "justify-center" : "justify-between"}`}>
          <Brand compact={compact} />
          {isDesktop && (
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? "Expand navigation sidebar" : "Collapse navigation sidebar"}
              aria-pressed={isCollapsed}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-surface-800 hover:text-white"
            >
              {isCollapsed ? <ChevronDoubleRightIcon aria-hidden="true" className="h-4 w-4" /> : <ChevronDoubleLeftIcon aria-hidden="true" className="h-4 w-4" />}
            </button>
          )}
        </div>

        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4 md:hidden">
          <span className="text-[10px] font-semibold tracking-[0.16em] text-gray-500">NAVIGATION</span>
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setIsOpen(false)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-surface-800 hover:text-white"
          >
            <XMarkIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5" aria-label="Main navigation">
          {renderNavigationItems()}
        </nav>

        <div className="shrink-0 border-t border-line p-3">
          <button
            type="button"
            title={compact ? "Buy Votes" : undefined}
            aria-current={activeItem === "Buy Votes" ? "page" : undefined}
            onClick={() => navigateTo("/buy-votes")}
            className={`flex min-h-11 w-full items-center rounded-lg bg-accent px-3 text-sm font-semibold text-white transition-colors hover:bg-accent-hover active:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${compact ? "justify-center" : "gap-3"}`}
          >
            <ShoppingBagIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
            {!compact && <span>Buy Votes</span>}
          </button>
          <div className="mt-2 border-t border-line pt-2">
            <button
              type="button"
              title={compact ? "Profile" : undefined}
              aria-current={activeItem === "Profile" ? "page" : undefined}
              onClick={() => navigateTo("/profile")}
              className={`flex min-h-10 w-full items-center rounded-lg px-3 text-sm font-medium transition-colors hover:bg-surface-800 hover:text-white ${compact ? "justify-center" : "gap-3"} ${activeItem === "Profile" ? "text-white" : "text-gray-400"}`}
            >
              <UserCircleIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
              {!compact && <span>Profile</span>}
            </button>
            <button
              type="button"
              title={compact ? "Log out" : undefined}
              onClick={handleLogout}
              className={`flex min-h-10 w-full items-center rounded-lg px-3 text-sm font-medium text-gray-500 transition-colors hover:bg-surface-800 hover:text-accent-soft ${compact ? "justify-center" : "gap-3"}`}
            >
              <ArrowLeftStartOnRectangleIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
              {!compact && <span>Log out</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function Brand({ compact = false }) {
  return (
    <div className="flex min-w-0 items-center gap-3" aria-label="DESAC TV">
      <div aria-hidden="true" className="h-9 w-9 shrink-0 rounded-lg bg-logo-192 bg-cover bg-center" />
      {!compact && (
        <span className="whitespace-nowrap text-base font-bold tracking-tight text-white">
          DESAC<span className="text-accent">TV</span>
        </span>
      )}
    </div>
  );
}

export default Navbar;
